import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, TYPE_META } from "@/lib/api";
import { motion } from "framer-motion";
import { Star, ExternalLink, Trash2, ArrowLeft, ArrowRight, X, Plus, Tv, CheckCircle2, Share2, Pencil, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Flourish, Fleuron } from "@/components/Ornaments";
import Rating, { StatusPill } from "@/components/Rating";
import ShareToCommonsModal from "@/components/ShareToCommonsModal";
import NoteEditor from "@/components/NoteEditor";
import NoteBody from "@/components/NoteBody";

// Per-media-type labels for the "played" toggle so the same underlying flag
// reads naturally on every shelf. Types omitted here (picture, artist, unknown)
// simply won't render the toggle.
const PLAYED_LABELS = {
  movie:   { done: "Watched",    action: "Mark as watched"  },
  tvshow:  { done: "Watched",    action: "Mark as watched"  },
  video:   { done: "Watched",    action: "Mark as watched"  },
  music:   { done: "Listened",   action: "Mark as listened" },
  article: { done: "Read",       action: "Mark as read"     },
  book:    { done: "Read",       action: "Mark as read"     },
  recipe:  { done: "Cooked",     action: "Mark as cooked"   },
};

function formatStamp(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" }).toUpperCase();
}

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [collections, setCollections] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null, position: 0, total: 0 });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);

  useEffect(() => {
    let cancel = false;
    // Fetch the item + its neighbors first (the two things that gate the paint
    // and the nav arrows). Collections are only needed once the user opens the
    // add-to-collection picker, so they lazy-load below.
    (async () => {
      try {
        const [itemRes, nbRes] = await Promise.all([
          api.get(`/items/${id}`),
          api.get(`/items/${id}/neighbors`),
        ]);
        if (cancel) return;
        setItem(itemRes.data);
        setNeighbors(nbRes.data || { prev: null, next: null, position: 0, total: 0 });
      } catch (e) {
        if (cancel) return;
        console.error(`[item:${id}] load failed:`, e);
        toast.error("Couldn't find that item");
        navigate("/");
        return;
      }
      // Collections load in the background, no gate on paint.
      try {
        const colsRes = await api.get(`/collections`);
        if (!cancel) setCollections(colsRes.data || []);
      } catch { /* not critical for viewing */ }
    })();
    return () => { cancel = true; };
  }, [id, navigate]);

  // Prefetch neighbour item bodies so tapping ← / → paints instantly. Uses a
  // low-priority GET the browser can hold in its HTTP cache.
  useEffect(() => {
    const prefetch = (nid) => { if (nid) api.get(`/items/${nid}`).catch(() => {}); };
    prefetch(neighbors.prev?.id);
    prefetch(neighbors.next?.id);
  }, [neighbors]);

  // Keyboard navigation: ← / → cycle through the current shelf.
  useEffect(() => {
    function onKey(e) {
      if (e.target.matches?.("input, textarea, [contenteditable=true]")) return;
      if (e.key === "ArrowLeft" && neighbors.prev) navigate(`/item/${neighbors.prev.id}`);
      else if (e.key === "ArrowRight" && neighbors.next) navigate(`/item/${neighbors.next.id}`);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [neighbors, navigate]);

  // Mobile swipe: horizontal swipe on the page body navigates between siblings.
  const touchRef = useRef({ x: 0, y: 0, t: 0 });
  function onTouchStart(e) {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }
  function onTouchEnd(e) {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    if (dt > 600) return;
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx) * 0.8) return;
    if (dx < 0 && neighbors.next) navigate(`/item/${neighbors.next.id}`);
    else if (dx > 0 && neighbors.prev) navigate(`/item/${neighbors.prev.id}`);
  }

  const meta = useMemo(() => TYPE_META[item?.type] || TYPE_META.unknown, [item]);

  if (!item) {
    return (
      <p className="font-cinzel text-xs tracking-[0.26em] uppercase text-[var(--ink-dusty)]">
        Opening the page…
      </p>
    );
  }

  async function toggleFavorite() {
    const r = await api.patch(`/items/${item.id}`, { favorite: !item.favorite });
    setItem(r.data);
  }

  async function updateRating(rating) {
    // rating is either {scale, value} or null to clear.
    try {
      const r = await api.patch(`/items/${item.id}`, { rating: rating || {} });
      setItem(r.data);
    } catch { toast.error("Could not save rating"); }
  }

  async function updateStatus(status) {
    // status is "finished" | "dnf" | null. Empty string clears server-side.
    try {
      const r = await api.patch(`/items/${item.id}`, { status: status ?? "" });
      setItem(r.data);
    } catch { toast.error("Could not save"); }
  }

  async function toggleWatched() {
    // `played` reads as "Watched" / "Read" / "Listened" / "Cooked" depending
    // on media type (see PLAYED_LABELS). Same field for every shelf so we don't
    // need a bespoke flag per type. Also excludes the item from Today's Pick.
    try {
      const next = !item.played;
      const r = await api.patch(`/items/${item.id}`, { played: next });
      setItem(r.data);
      const lbl = PLAYED_LABELS[item.type];
      toast.success(next
        ? `Marked ${lbl ? lbl.done.toLowerCase() : "done"}`
        : "Moved back to unread");
    } catch { toast.error("Could not update"); }
  }

  async function deleteItem() {
    try {
      await api.delete(`/items/${item.id}`);
      toast.success("Removed from the shelf");
      navigate("/");
    } catch (e) {
      console.error("[item:delete] failed:", e);
      toast.error("Could not remove that item");
    }
  }

  async function addTag(e) {
    e?.preventDefault();
    const t = tagInput.trim();
    if (!t) return;
    if ((item.tags || []).includes(t)) { setTagInput(""); return; }
    const tags = [...(item.tags || []), t];
    const r = await api.patch(`/items/${item.id}`, { tags });
    setItem(r.data);
    setTagInput("");
  }

  async function removeTag(t) {
    const tags = (item.tags || []).filter(x => x !== t);
    const r = await api.patch(`/items/${item.id}`, { tags });
    setItem(r.data);
  }

  async function toggleCollection(colId) {
    const has = (item.collection_ids || []).includes(colId);
    const collection_ids = has
      ? item.collection_ids.filter(c => c !== colId)
      : [...(item.collection_ids || []), colId];
    const r = await api.patch(`/items/${item.id}`, { collection_ids });
    setItem(r.data);
    toast.success(has ? "Removed from collection" : "Added to collection");
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto relative"
      data-testid="item-detail"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)] hover:text-[var(--bronze)]"
          data-testid="back-btn"
        >
          <ArrowLeft size={12}/> Back to library
        </button>
      </div>

      {/* Prev / next siblings — a subtle bottom pill so nothing overlays the
       *  card content. Sits above the mobile bottom nav (bottom-24) and above
       *  the desktop margin (md:bottom-8). Always visible when there's a
       *  sibling to move to; keyboard ← → + touch swipe still work. */}
      {(neighbors.prev || neighbors.next) && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-8 z-30 flex items-center gap-1 bg-[var(--card-bg)]/95 backdrop-blur-sm border border-[rgba(140,106,47,0.28)] rounded-full shadow-lg px-1.5 py-1"
          data-testid="item-nav-pager"
        >
          <button
            onClick={() => neighbors.prev && navigate(`/item/${neighbors.prev.id}`)}
            disabled={!neighbors.prev}
            data-testid="prev-item-btn"
            aria-label={neighbors.prev ? `Previous: ${neighbors.prev.title || "item"}` : "No previous"}
            title={neighbors.prev?.title || ""}
            className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--ink-dusty)] hover:text-[var(--bronze)] disabled:opacity-30 disabled:hover:text-[var(--ink-dusty)] active:scale-95 transition-all"
          >
            <ArrowLeft size={16} strokeWidth={1.5}/>
          </button>
          <span className="font-mono-lc text-[10px] tracking-[0.18em] uppercase text-[var(--ink-light)] px-2 select-none" data-testid="item-position">
            {neighbors.position || "?"} / {neighbors.total || "?"}
          </span>
          <button
            onClick={() => neighbors.next && navigate(`/item/${neighbors.next.id}`)}
            disabled={!neighbors.next}
            data-testid="next-item-btn"
            aria-label={neighbors.next ? `Next: ${neighbors.next.title || "item"}` : "No next"}
            title={neighbors.next?.title || ""}
            className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--ink-dusty)] hover:text-[var(--bronze)] disabled:opacity-30 disabled:hover:text-[var(--ink-dusty)] active:scale-95 transition-all"
          >
            <ArrowRight size={16} strokeWidth={1.5}/>
          </button>
        </div>
      )}

      <div className="mt-8 flex items-center gap-3">
        <span className="font-cinzel text-[10px] tracking-[0.26em] uppercase" style={{ color: meta.accent }}>{meta.label}</span>
        <span className="text-[var(--ink-light)]">·</span>
        <span className="stamp" data-testid="item-date-stamp" style={{ "--stamp-color": meta.accent }}>
          {formatStamp(item.date_added)}
        </span>
      </div>

      <h1 className="mt-5 font-serif-lit text-5xl sm:text-6xl tracking-tight text-[var(--ink)] leading-[1.05]">
        {item.title || "Untitled"}
      </h1>
      {item.creator && (
        <p className="mt-3 font-garamond italic text-2xl text-[var(--ink-dusty)]">{item.creator}</p>
      )}

      {/* Primary actions — kept near the top so the source link is visible above the fold */}
      <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="primary-actions">
        {item.source_url && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noreferrer"
            className="lc-btn flex items-center gap-2"
            data-testid="open-source-btn-top"
            onClick={() => {
              if (!item.played) api.patch(`/items/${item.id}`, { played: true }).catch(() => {});
            }}
          >
            <ExternalLink size={14} strokeWidth={1.5}/> Open source
          </a>
        )}
        <button onClick={toggleFavorite} className="lc-btn-ghost flex items-center gap-2" data-testid="favorite-btn-top">
          <Star size={14} strokeWidth={1.4} className={item.favorite ? "fill-[var(--gold)] text-[var(--gold)]" : ""}/>
          {item.favorite ? "Favorited" : "Favorite"}
        </button>
        {PLAYED_LABELS[item.type] && (
          <button
            onClick={toggleWatched}
            className={`lc-btn-ghost flex items-center gap-2 transition-colors ${item.played ? "border-[var(--sage)] text-[var(--sage)] bg-[rgba(122,138,102,0.08)]" : ""}`}
            data-testid="watched-toggle-btn"
          >
            <CheckCircle2 size={14} strokeWidth={1.4} className={item.played ? "text-[var(--sage)]" : ""}/>
            {item.played ? PLAYED_LABELS[item.type].done : PLAYED_LABELS[item.type].action}
          </button>
        )}
      </div>

      {/* Rating widget — scale varies by media type (see Rating.jsx).
          Books also get a separate Finished/DNF pill next to the stars, kept
          in Item.status (not rating) so both signals coexist. */}
      <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="rating-row">
        <span className="font-cinzel text-[10px] tracking-[0.24em] uppercase text-[var(--ink-dusty)]">
          {item.type === "recipe" ? "Verdict" : "Rating"}
        </span>
        <Rating
          type={item.type}
          value={item.rating || null}
          onChange={updateRating}
          testId="item-rating"
        />
        {item.type === "book" && (
          <StatusPill value={item.status || null} onChange={updateStatus} testId="item-status" />
        )}
      </div>

      <Flourish color={meta.accent} className="mt-8 opacity-70" />

      {item.image_url && (
        <div
          className="mt-10 placed-card relative"
          style={{ "--tape-color": meta.tape, borderColor: meta.border }}
        >
          <span className="tape-corner tape-tl" />
          <span className="tape-corner tape-tr" />
          {/* Pictures render the FULL photo (object-contain, no hard height
              cap) so tall/portrait shots aren't cropped. Every other media
              type keeps the poster-style crop that reads as "cover art". */}
          {item.type === "picture" ? (
            <img
              src={item.image_url}
              alt=""
              className="block w-full h-auto max-h-[85vh] object-contain bg-[var(--paper-aged)] rounded-[2px]"
              data-testid="item-full-image"
            />
          ) : (
            <img
              src={item.image_url}
              alt=""
              className="block w-full max-h-[560px] object-cover rounded-[2px]"
              data-testid="item-full-image"
            />
          )}
        </div>
      )}

      {item.excerpt && (
        <blockquote
          className="mt-10 font-garamond italic text-xl text-[var(--ink-dusty)] border-l-2 pl-6 leading-relaxed"
          style={{ borderColor: meta.accent }}
        >
          &ldquo;{item.excerpt}&rdquo;
        </blockquote>
      )}

      {/* Where to watch — JustWatch search for movies & TV shows */}
      {(item.type === "movie" || item.type === "tvshow") && item.title && (
        <section className="mt-12" data-testid="where-to-watch">
          <div className="flex items-center gap-3 mb-3">
            <Fleuron size={16} color="var(--bronze)" />
            <p className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)]">
              Where to watch
            </p>
          </div>
          <a
            href={`https://www.justwatch.com/us/search?q=${encodeURIComponent(item.title)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="justwatch-link"
            className="inline-flex items-center gap-3 px-5 py-3 rounded-full border transition-all hover:bg-[var(--paper-aged)]"
            style={{ borderColor: meta.accent, color: meta.accent }}
          >
            <Tv size={14} strokeWidth={1.4} />
            <span className="font-cinzel text-[11px] tracking-[0.22em] uppercase">
              Find on JustWatch
            </span>
            <ExternalLink size={12} strokeWidth={1.4} />
          </a>
          <p className="mt-2 font-garamond italic text-[var(--ink-light)] text-sm">
            See which services are streaming &ldquo;{item.title}&rdquo; right now.
          </p>
        </section>
      )}

      {/* Handwritten note — opened in a dedicated full-screen editor so
          there's no ambiguity about where to tap. Button is the primary
          affordance; the preview box underneath is a bonus tap target. */}
      <section className="mt-14" data-testid="note-section">
        <div className="flex items-center gap-3 mb-3">
          <Fleuron size={16} color="var(--bronze)" />
          <p className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)]">A personal note</p>
        </div>
        <button
          type="button"
          onClick={() => setShowNoteEditor(true)}
          data-testid="open-note-editor-btn"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-cinzel text-[11px] tracking-[0.22em] uppercase text-[var(--card-bg)] bg-[var(--bronze)] hover:bg-[var(--ink)] active:scale-[0.98] transition-all shadow-sm"
        >
          {item.note ? (
            <>
              <NotebookPen size={14} strokeWidth={1.8} />
              Open note
            </>
          ) : (
            <>
              <Pencil size={14} strokeWidth={1.8} />
              Write a note
            </>
          )}
        </button>
        {item.note && (
          <button
            type="button"
            onClick={() => setShowNoteEditor(true)}
            data-testid="open-note-preview"
            aria-label="open note"
            className="mt-4 w-full text-left placed-card p-4 hover:border-[var(--bronze)] cursor-pointer transition-colors"
          >
            {/* Preview — render the markdown so **bold** / *italic* / bullet
                lists show up formatted (not with raw asterisks). line-clamp-2
                keeps the preview tidy; the whole box opens the editor. */}
            <NoteBody
              text={item.note}
              testId="note-preview-body"
              className="font-garamond italic text-[15px] text-[var(--ink)] leading-relaxed line-clamp-2"
            />
            <p className="mt-2 font-mono-lc text-[9px] tracking-[0.22em] uppercase text-[var(--ink-light)]">
              tap to open
            </p>
          </button>
        )}
      </section>

      {/* Tags */}
      <section className="mt-14">
        <div className="flex items-center gap-3 mb-3">
          <Fleuron size={16} color="var(--bronze)" />
          <p className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)]">Tags</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-testid="tag-list">
          {(item.tags || []).map(t => (
            <span key={t} className="chip" data-testid={`tag-${t}`}>
              {t}
              <button onClick={() => removeTag(t)} className="ml-1 hover:text-[var(--terracotta)]" data-testid={`remove-tag-${t}`}>
                <X size={11} strokeWidth={1.6}/>
              </button>
            </span>
          ))}
          <form onSubmit={addTag} className="flex items-center gap-1">
            <input
              data-testid="tag-input"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              placeholder="add a tag…"
              className="bg-transparent border-b border-[rgba(42,30,20,0.18)] focus:border-[var(--terracotta)] outline-none font-mono-lc text-xs tracking-[0.12em] py-1 px-1 w-32"
            />
            <button type="submit" data-testid="add-tag-btn" className="text-[var(--ink-dusty)] hover:text-[var(--bronze)]">
              <Plus size={14} strokeWidth={1.6}/>
            </button>
          </form>
        </div>
      </section>

      {/* Collections */}
      <section className="mt-14">
        <div className="flex items-center gap-3 mb-3">
          <Fleuron size={16} color="var(--bronze)" />
          <p className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)]">Add to collection</p>
        </div>
        {collections.length === 0 ? (
          <p className="font-garamond italic text-[var(--ink-dusty)]">
            No collections yet — start one from the Collections page.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="collections-picker">
            {collections.map(c => {
              const active = (item.collection_ids || []).includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleCollection(c.id)}
                  className={`chip ${active ? "active" : ""}`}
                  data-testid={`pick-collection-${c.id}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Actions */}
      <div className="mt-16 flex flex-wrap items-center gap-3 border-t border-[rgba(140,106,47,0.24)] pt-7">
        <button onClick={toggleFavorite} className="lc-btn-ghost flex items-center gap-2" data-testid="favorite-btn">
          <Star size={14} strokeWidth={1.4} className={item.favorite ? "fill-[var(--gold)] text-[var(--gold)]" : ""}/>
          {item.favorite ? "Favorited" : "Favorite"}
        </button>
        {item.source_url && (
          <a href={item.source_url} target="_blank" rel="noreferrer" className="lc-btn-ghost flex items-center gap-2" data-testid="open-source-btn">
            <ExternalLink size={14} strokeWidth={1.4}/> Open source
          </a>
        )}
        <button
          onClick={() => setShowShareModal(true)}
          className="lc-btn-ghost flex items-center gap-2"
          data-testid="share-commons-btn"
          title="Share this to the Commons"
        >
          <Share2 size={14} strokeWidth={1.4}/> Share to Commons
        </button>
        <button
          onClick={() => setConfirmOpen(true)}
          className="lc-btn-ghost flex items-center gap-2 text-[var(--terracotta)] border-[rgba(158,59,43,0.4)] hover:border-[var(--terracotta)]"
          data-testid="delete-btn"
        >
          <Trash2 size={14} strokeWidth={1.4}/> Remove
        </button>
      </div>

      {showShareModal && (
        <ShareToCommonsModal
          item={item}
          onClose={() => setShowShareModal(false)}
          onShared={() => setShowShareModal(false)}
        />
      )}

      {showNoteEditor && (
        <NoteEditor
          item={item}
          onClose={() => setShowNoteEditor(false)}
          onSaved={(updated) => setItem(prev => prev ? { ...prev, note: updated.note } : prev)}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[var(--card-bg)] border border-[rgba(140,106,47,0.3)]" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-lit text-2xl text-[var(--ink)]">Take this off the shelf?</AlertDialogTitle>
            <AlertDialogDescription className="font-garamond italic text-[var(--ink-dusty)]">
              This will permanently remove &ldquo;{item.title}&rdquo; from your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn" className="lc-btn-ghost">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteItem}
              data-testid="confirm-delete-btn"
              className="lc-btn"
              style={{ background: "var(--terracotta)", borderColor: "var(--terracotta)" }}
            >
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
